import { MessageCircle, Mail, ArrowUpRight } from "lucide-react";
import { useContent, useLang } from "@/context/AppContext";
import { Reveal } from "@/components/ui-bits";

// Contact — the closing spread. Deep warm charcoal (never flat black),
// ivory type, quiet burnt-sienna/gold accents reserved for hairlines and
// hover states. Palette: ivory paper / ink / burnt-sienna / gold only.
export default function Contact() {
  const { t } = useLang();
  const { content } = useContent();
  const s = content?.settings || {};
  const whatsapp = s.whatsapp_link || "#";
  const email = s.email || "";

  const indexItems = [
    { label: "WhatsApp", value: s.whatsapp_number || "—", testid: "contact-index-whatsapp" },
    { label: "Email", value: email || "—", testid: "contact-index-email" },
    { label: "Instagram", value: `@${s.instagram || "—"}`, testid: "contact-index-instagram" },
  ];

  return (
    <section
      id="contact"
      className="grain-overlay relative overflow-hidden bg-[#16120D] py-24 sm:py-32"
      data-testid="contact-section"
    >
      {/* quiet depth: warm sienna glow rising from the top edge — keeps the
          charcoal from reading as flat black, stays under the grain */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 bg-[radial-gradient(120%_85%_at_50%_0%,rgba(178,75,21,0.16),transparent_58%)]"
      />
      <div className="relative mx-auto max-w-7xl px-4 sm:px-8 lg:px-16">
        <div className="max-w-4xl">
          <Reveal>
            <div className="flex items-center gap-4">
              <span aria-hidden="true" className="h-px w-10 bg-gold/60" />
              <p className="font-mono text-[11px] font-bold uppercase tracking-[0.3em] text-gold/90">
                {t("contactEyebrow")} — 07
              </p>
            </div>
          </Reveal>
          <Reveal delay={0.08}>
            <h2
              className="mt-6 font-display text-[clamp(2.4rem,5.6vw+0.5rem,5.25rem)] font-medium leading-[1.03] tracking-[-0.02em] text-paper [&_em]:font-light [&_em]:italic [&_em]:text-gold"
              data-testid="contact-headline"
            >
              {t("contactTitle")}
            </h2>
          </Reveal>
          <Reveal delay={0.16}>
            <p className="mt-8 max-w-xl text-base leading-relaxed text-paper/75 sm:text-lg" data-testid="contact-body">
              {t("contactBody")}
            </p>
          </Reveal>
          <Reveal delay={0.24}>
            <div className="mt-12 flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center">
              <a
                href={whatsapp}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="whatsapp-contact-cta-button"
                className="group inline-flex items-center justify-center gap-3 bg-paper px-8 py-4 font-mono text-[11px] font-bold uppercase tracking-[0.18em] text-ink transition-all duration-300 hover:-translate-y-0.5 hover:shadow-[0_14px_32px_-14px_rgba(229,169,60,0.5)]"
              >
                <MessageCircle className="h-4 w-4" />
                {t("whatsappCta")}
                <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </a>
              <a
                href={email ? `mailto:${email}` : "#"}
                data-testid="email-contact-cta-button"
                className="inline-flex items-center justify-center gap-3 border border-paper/30 px-8 py-4 font-mono text-[11px] font-bold uppercase tracking-[0.18em] text-paper/90 transition-colors duration-300 hover:border-gold hover:text-gold"
              >
                <Mail className="h-4 w-4" />
                {t("emailCta")}
              </a>
            </div>
          </Reveal>
        </div>

        {/* editorial contact index — 3-up grid with hairline dividers on sm+,
            evenly stacked on mobile */}
        <Reveal delay={0.3}>
          <div className="mt-16 grid gap-10 border-t border-gold/25 pt-10 sm:grid-cols-3 sm:gap-0 sm:pt-8">
            {indexItems.map((item, i) => (
              <div
                key={item.label}
                data-testid={item.testid}
                className={
                  i === 0
                    ? "sm:pr-8"
                    : "sm:border-l sm:border-paper/15 sm:pl-8"
                }
              >
                <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-paper/60">{item.label}</p>
                <p className="mt-1.5 font-display text-lg leading-snug text-paper break-words">{item.value}</p>
              </div>
            ))}
          </div>
        </Reveal>
      </div>
    </section>
  );
}
